import { useMemo, useState } from "react";

import FavoriteIcon from "@mui/icons-material/Favorite";
import RefreshIcon from "@mui/icons-material/Refresh";
import {
  Alert,
  Box,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  LinearProgress,
  MenuItem,
  Paper,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { type BurnoutRow, type RiskLevel, listBurnoutRisk } from "@/api/burnout";
import { listOrganizations } from "@/api/organizations";
import { usePermission } from "@/auth/usePermission";
import { PageHeader } from "@/components/PageHeader";

const RISK_COLOR: Record<RiskLevel, "default" | "info" | "warning" | "error"> = {
  LOW: "info",
  MEDIUM: "warning",
  HIGH: "error",
  CRITICAL: "error",
};

function ScoreBar({ score }: { score: number }) {
  const color =
    score >= 76 ? "error" : score >= 56 ? "warning" : score >= 31 ? "info" : "success";
  return (
    <Stack direction="row" spacing={1} alignItems="center" sx={{ minWidth: 160 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress variant="determinate" value={score} color={color} />
      </Box>
      <Typography variant="caption" sx={{ minWidth: 28, textAlign: "right" }}>
        {score}
      </Typography>
    </Stack>
  );
}

export function BurnoutPage() {
  const { t } = useTranslation();
  const canRead = usePermission("burnout:read_all");

  const [days, setDays] = useState(30);
  const [orgId, setOrgId] = useState<string>("");

  const orgs = useQuery({
    queryKey: ["organizations-active"],
    queryFn: () => listOrganizations({ status: "ACTIVE", limit: 100 }),
    enabled: canRead,
  });

  const data = useQuery({
    queryKey: ["burnout-risk", days, orgId],
    queryFn: () =>
      listBurnoutRisk({
        days,
        organization_id: orgId || undefined,
      }),
    enabled: canRead,
  });

  const counts = useMemo(() => {
    const rows = data.data ?? [];
    const result = { LOW: 0, MEDIUM: 0, HIGH: 0, CRITICAL: 0 };
    for (const r of rows) result[r.risk_level] += 1;
    return result;
  }, [data.data]);

  if (!canRead) {
    return (
      <Box>
        <PageHeader title={t("burnout.title")} subtitle={t("burnout.subtitle")} helpTerm="surcharge" />
        <Alert severity="warning">{t("burnout.forbidden")}</Alert>
      </Box>
    );
  }

  return (
    <Box>
      <PageHeader
        title={t("burnout.title")}
        subtitle={t("burnout.subtitle")}
        actions={
          <IconButton onClick={() => data.refetch()} aria-label={t("common.retry")}>
            <RefreshIcon />
          </IconButton>
        }
      />

      {/* Stats */}
      <Stack direction="row" spacing={1.5} sx={{ mb: 3, flexWrap: "wrap" }}>
        {(["LOW", "MEDIUM", "HIGH", "CRITICAL"] as RiskLevel[]).map((level) => (
          <Paper key={level} variant="outlined" sx={{ p: 1.5, minWidth: 140, flex: 1 }}>
            <Typography variant="caption" color="text.secondary" component="div">
              {t(`burnout.level.${level}`)}
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Typography
                variant="h5"
                sx={{ fontWeight: 700 }}
                color={
                  level === "CRITICAL"
                    ? "error.main"
                    : level === "HIGH"
                      ? "warning.main"
                      : "text.primary"
                }
              >
                {counts[level]}
              </Typography>
              <FavoriteIcon
                fontSize="small"
                color={
                  RISK_COLOR[level] === "default"
                    ? "disabled"
                    : (RISK_COLOR[level] as "info" | "warning" | "error")
                }
              />
            </Stack>
          </Paper>
        ))}
      </Stack>

      {/* Filters */}
      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={2}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>{t("burnout.window")}</InputLabel>
          <Select
            label={t("burnout.window")}
            value={days}
            onChange={(e) => setDays(Number(e.target.value))}
          >
            {[7, 14, 30, 60, 90].map((d) => (
              <MenuItem key={d} value={d}>
                ±{d} {t("burnout.days")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 240 }}>
          <InputLabel>{t("burnout.org")}</InputLabel>
          <Select
            label={t("burnout.org")}
            value={orgId}
            onChange={(e) => setOrgId(e.target.value)}
          >
            <MenuItem value="">{t("burnout.allOrgs")}</MenuItem>
            {(orgs.data?.items ?? []).map((o) => (
              <MenuItem key={o.id} value={o.id}>
                {o.code} — {o.name}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Table */}
      {data.isLoading ? (
        <CircularProgress />
      ) : (data.data ?? []).length === 0 ? (
        <Alert severity="info">{t("burnout.empty")}</Alert>
      ) : (
        <Paper variant="outlined" sx={{ overflow: "auto" }}>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell>{t("burnout.col.practitioner")}</TableCell>
                <TableCell>{t("burnout.col.level")}</TableCell>
                <TableCell sx={{ minWidth: 180 }}>{t("burnout.col.score")}</TableCell>
                <TableCell align="right">{t("burnout.col.shifts")}</TableCell>
                <TableCell align="right">{t("burnout.col.perWeek")}</TableCell>
                <TableCell align="right">{t("burnout.col.nightPct")}</TableCell>
                <TableCell align="right">{t("burnout.col.weekendPct")}</TableCell>
                <TableCell align="right">{t("burnout.col.streak")}</TableCell>
                <TableCell align="right">{t("burnout.col.hours")}</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {(data.data ?? []).map((r: BurnoutRow) => (
                <TableRow key={r.practitioner_id} hover>
                  <TableCell>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      {r.last_name}
                      {r.first_initial && ` ${r.first_initial}.`}
                    </Typography>
                    {r.rpps && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontFamily: "monospace", fontSize: 11 }}
                      >
                        {r.rpps}
                      </Typography>
                    )}
                  </TableCell>
                  <TableCell>
                    <Chip
                      size="small"
                      color={RISK_COLOR[r.risk_level]}
                      label={t(`burnout.level.${r.risk_level}`)}
                    />
                  </TableCell>
                  <TableCell>
                    <ScoreBar score={r.score} />
                  </TableCell>
                  <TableCell align="right">{r.shifts_total}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                    {(r.weighted_hours / Math.max(1, days / 7)).toFixed(1)}
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                    {(r.night_shift_pct * 100).toFixed(0)}%
                  </TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                    {(r.weekend_shift_pct * 100).toFixed(0)}%
                  </TableCell>
                  <TableCell align="right">{r.max_consecutive_days}</TableCell>
                  <TableCell align="right" sx={{ fontFamily: "monospace" }}>
                    {r.weighted_hours.toFixed(1)}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </Paper>
      )}
    </Box>
  );
}
